import {
    addDays,
    format,
    parseISO,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears,
} from 'date-fns';
import type { FigureRange } from 'types/homeWidgetFigures';
import type { HomeWidgetPeriod } from 'types/homeWidgets';

/** A period with dates of its own: every one but a saved view's. */
export type DatedPeriod = Exclude<HomeWidgetPeriod, 'AS_SAVED'>;

const DAY = 'yyyy-MM-dd';

const START: Record<DatedPeriod, (end: Date) => Date> = {
    TODAY: (end) => end,
    THIS_WEEK: (end) => startOfWeek(end, { weekStartsOn: 1 }),
    THIS_MONTH: startOfMonth,
    YEAR_TO_DATE: startOfYear,
    LAST_12_MONTHS: (end) => addDays(subMonths(end, 12), 1),
};

const EARLIER: Record<DatedPeriod, (asOf: Date) => Date> = {
    TODAY: (asOf) => subDays(asOf, 1),
    THIS_WEEK: (asOf) => subWeeks(asOf, 1),
    THIS_MONTH: (asOf) => subMonths(asOf, 1),
    YEAR_TO_DATE: (asOf) => subYears(asOf, 1),
    LAST_12_MONTHS: (asOf) => subYears(asOf, 1),
};

function endingOn(period: DatedPeriod, end: Date): FigureRange {
    return { from: format(START[period](end), DAY), to: format(end, DAY) };
}

/**
 * The days a period covers, both ends included, by the rules the backend applies to a catalogue figure: every period
 * ends on `asOf`, the viewer's today as `yyyy-MM-dd`, and a week starts on Monday.
 */
export function periodRange(period: DatedPeriod, asOf: string): FigureRange {
    return endingOn(period, parseISO(asOf));
}

/**
 * The same elapsed span of the period before: the period as it stood one day, week, month or year earlier. A day its
 * target month lacks moves back to that month's last, so 31 March compares with the whole of February.
 */
export function previousPeriodRange(period: DatedPeriod, asOf: string): FigureRange {
    return endingOn(period, EARLIER[period](parseISO(asOf)));
}
